<template>
  <div>
    <core-progress :in-progress="$wait.is('loadingMeasurement')" />
    <title-bar :show-back="true">
      <ProjectSelector
        class="col-5"
        @project-change="mixin_onProjectChange"
      />
    </title-bar>
    <div class="grid-container">
      <div
        v-if="previewMap"
        class="iri-map"
      >
        <MeasurementsMap
          :iri-runs="segments"
          :start-project-location="projectLocation"
          @markerUpdated="markerUpdated($event)"
        />
      </div>
      <div class="iri-form">
        <material-card
          outlined
          class="fill-height"
        >
          <v-form ref="calibrationForm">
            <v-select
              v-model="selectedType"
              :items="translatedFileType"
              :label="$t('views.upload.placeholder')"
              :rules="[(v) => !!v || $t('api-error.iri.datapoint-types-error')]"
              class="select-no-border"
            />
            <v-autocomplete
              v-if="isRoadroidApi"
              v-model="selectedRoadroidMeasurements"
              class="select-no-border"
              multiple
              :rules="[validFile]"
              :items="availableRoadroidMeasurements"
              @input="onInput"
            />
            <v-file-input
              v-else
              v-model="files"
              :accept="validFileExtensions"
              :rules="[validFile]"
              :label="$t('views.iri-management.upload-form.calibration-files')"
              :clearable="false"
              multiple
              counter
              small-chips
              class="px-4"
              @change="onFileChange"
            />
            <template v-if="preview">
              <v-text-field
                v-model="name"
                :label="$t('enums.headers.name')"
                :rules="[(v) => !!v || $t('api-error.iri.name-error')]"
              />
              <v-select
                v-model="selectedLayer"
                :items="roadLayers"
                :label="$t('enums.headers.layer')"
                :rules="[(v) => !!v || $t('api-error.iri.layer-error')]"
                class="select-no-border"
              />
              <v-select
                v-if="!class1Types.includes(selectedType)"
                :items="deviceVehiclePairs"
                :label="
                  $t('views.iri-management.upload-form.device-vehicle-pair')
                "
                :rules="[deviceVehiclePairRule]"
                class="select-no-border"
                @change="onCalibrationSelect"
              />
              <v-select
                v-model="selectedRoadCategory"
                :items="translatedRoadCategories"
                :label="$t('enums.headers.roadCategory')"
                :rules="[(v) => !!v || $t('api-error.iri.roadCategory-error')]"
                class="select-no-border"
              />
              <v-select
                v-model="selectedDirection"
                :items="roadDirections"
                :label="$t('enums.headers.direction')"
                :rules="[(v) => !!v || $t('api-error.iri.roadDirection-error')]"
                class="select-no-border"
              />
              <v-select
                v-model="selectedLaneNumber"
                :items="laneNumbers"
                :label="$t('enums.headers.laneNumber')"
                :rules="[(v) => !!v || $t('api-error.iri.laneNumber-error')]"
                class="select-no-border"
              />
              <v-text-field
                v-model="sectionStart"
                :label="$t('enums.headers.sectionStart')"
                :hint="$t('enums.headers.sectionInputsHint')"
                :rules="[sectionStartRule]"
              />
              <v-text-field
                v-model="roadWidth"
                :label="$t('enums.headers.roadWidth')"
                :rules="[roadWidthRule]"
                type="number"
              />
              <v-text-field
                v-model="shoulderWidth"
                :label="$t('enums.headers.shoulderWidth')"
                :rules="[shoulderWidthRule]"
                type="number"
              />
              <v-text-field
                v-model="bikeLaneWidth"
                :label="$t('enums.headers.bikeLaneWidth')"
                :rules="[shoulderWidthRule]"
                type="number"
              />
              <template v-if="class1Types.includes(selectedType)">
                <v-checkbox
                  v-model="isReference"
                  dense
                >
                  <template #label>
                    <div>
                      {{ $t('views.iri-management.upload-form.is-reference') }}
                    </div>
                  </template>
                </v-checkbox>
                <v-checkbox
                  v-model="isPublic"
                  dense
                >
                  <template #label>
                    <div>
                      {{ $t('views.iri-management.upload-form.is-public') }}
                    </div>
                  </template>
                </v-checkbox>
                <v-checkbox
                  v-model="macrotextureImport"
                  dense
                >
                  <template #label>
                    <div>
                      {{ $t('views.iri-management.checkbox-label') }}
                    </div>
                  </template>
                </v-checkbox>
              </template>
              <template v-if="isRoadroidApi">
                <v-checkbox
                  v-model="importImages"
                  dense
                >
                  <template #label>
                    <div>
                      {{ $t('views.iri-management.upload-form.import-images') }}
                    </div>
                  </template>
                </v-checkbox>
              </template>
            </template>
            <v-btn
              color="primary"
              block
              :disabled="!previewCompleted"
              @click="sendFile"
            >
              {{ $t('views.upload.confirmation') }}
            </v-btn>
          </v-form>
        </material-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import {
  iriFileTypes,
  roadLayers,
  roadCategories,
  roadDirections,
} from '@/utils/enum'
import { toSelectItems } from '@/utils/i18n'
import TitleBar from '@/components/core/TitleBar'
import MeasurementsMap from './Map.vue'
import ProjectSelector from '../../../core/ProjectSelector.vue'
import { romdasPreProcessing } from '@/utils/mdbUtil'
import { defaultGeolocation } from '@/utils/constants'
import projectsListMixin from '@/mixins/projectsListMixin'
import { isValidNumber } from '@/utils/math'

export default {
  name: 'IRIMeasurementCreateView',
  components: {
    MeasurementsMap,
    TitleBar,
    ProjectSelector,
  },

  mixins: [projectsListMixin],

  data() {
    return {
      submitButtonLoading: false,
      macrotextureImport: false,
      isReference: false,
      importImages: true,
      isPublic: false,
      files: null,
      types: Object.values(iriFileTypes),
      class1Types: [iriFileTypes.romdas],
      roadLayers: toSelectItems(roadLayers, 'enums.layers'),
      selectedLayer: null,
      translatedRoadCategories: toSelectItems(
        roadCategories,
        'enums.roadCategories',
      ),
      translatedFileType: toSelectItems(
        {
          1: iriFileTypes.romdas,
          2: iriFileTypes.roadroid,
          3: iriFileTypes.roadroidSingle,
          4: iriFileTypes.roadroidApi,
          5: iriFileTypes.roadroidApiSingle,
        },
        'enums.iri-file-types',
      ),
      roadDirections: toSelectItems(roadDirections, 'enums.roadDirections'),
      laneNumbers: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      preview: false,
      previewMap: false,
      previewCompleted: false,
      defaultMarkerUpdated: false,
      name: '',
      selectedType: '',
      selectedVehicle: null,
      selectedDevice: null,
      selectedRoadCategory: null,
      selectedDirection: null,
      selectedLaneNumber: null,
      sectionStart: '0+000',
      roadWidth: null,
      shoulderWidth: null,
      bikeLaneWidth: null,
      marker: defaultGeolocation,
      selectedRoadroidMeasurements: null,
    }
  },

  computed: {
    ...mapState('iri', [
      'calibrations',
      'segments',
      'roadroidMeasurementsList',
    ]),
    ...mapGetters('iri', ['deviceVehiclePairs']),
    ...mapGetters(['projectLocation']),

    validFileExtensions() {
      switch (this.selectedType) {
        case iriFileTypes.romdas:
          return '.mdb'
        case iriFileTypes.roadBump:
          return '.csv'
        case iriFileTypes.roadroid:
        case iriFileTypes.roadroidSingle:
          return '.txt'
        default:
          return ''
      }
    },

    availableRoadroidMeasurements() {
      return this.roadroidMeasurementsList.map((measurement) => ({
        text: `${measurement.RoadId} - (${measurement.ImportHistoryId})`,
        value: measurement.ImportHistoryId,
      }))
    },

    isRoadroidApi() {
      return (
        this.selectedType === iriFileTypes.roadroidApi ||
        this.selectedType === iriFileTypes.roadroidApiSingle
      )
    },

    isRoadroidApiSingle() {
      return this.selectedType === iriFileTypes.roadroidApiSingle
    },
  },

  watch: {
    async selectedType() {
      if (this.isRoadroidApi) {
        this.$wait.start('loadingMeasurement')
        try {
          await this.getRoadroidMeasurementList()
        } catch (error) {
          this.showDanger(error)
        } finally {
          this.$wait.end('loadingMeasurement')
        }
      }
    },
  },

  created() {
    this.setMeasurements([])
    this.setCalibrations([])
  },

  methods: {
    ...mapActions('iri', [
      'previewIriFile',
      'uploadIriData',
      'uploadCalibration',
      'getCalibrations',
      'getDevices',
      'getVehicles',
      'getRoadroidMeasurementList',
      'previewRoadroidMeasurements',
      'importRoadroidMeasurements',
      'importImagesForMeasurement',
    ]),
    ...mapMutations('iri', ['setCalibrations', 'setMeasurements']),
    ...mapMutations(['showDanger', 'showSuccess']),

    resetForm() {
      this.macrotextureImport = false
      this.isReference = false
      this.importImages = true
      this.isPublic = false
      this.selectedVehicle = null
      this.selectedDevice = null
      this.selectedLayer = null
      this.selectedRoadCategory = null
      this.selectedDirection = null
      this.selectedLaneNumber = null
      this.sectionStart = '0+000'
      this.roadWidth = null
      this.shoulderWidth = null
      this.bikeLaneWidth = null
      this.marker = defaultGeolocation
      this.defaultMarkerUpdated = false
    },

    async importMeasurement() {
      try {
        if (!this.$refs.calibrationForm.validate()) {
          return
        }

        this.submitButtonLoading = true
        this.$wait.start('loadingMeasurement')

        const result = await this.importRoadroidMeasurements({
          importHistoryIds: this.selectedRoadroidMeasurements,
          payload: {
            sectionStart: this.sectionStart,
            startLocation: this.defaultMarkerUpdated ? this.marker : undefined,
            roadCategory: this.selectedRoadCategory,
            roadWidth: this.roadWidth,
            shoulderWidth: this.shoulderWidth,
            bikeLaneWidth: this.bikeLaneWidth,
            direction: this.selectedDirection,
            lane: this.selectedLaneNumber,
            layer: this.selectedLayer,
            type: this.selectedType,
            name: this.name,
          },
        })

        if (this.importImages && result) {
          await this.importImagesForMeasurement(result._id)
        }

        this.$refs.calibrationForm.reset()

        this.macrotextureImport = false
        this.isReference = false
        this.importImages = true
        this.isPublic = false
        this.submitButtonLoading = false

        this.previewMap = false
        this.preview = false
        this.defaultMarkerUpdated = false

        this.showSuccess(
          this.$t('views.iri-management.upload-form.upload-file-successful'),
        )
      } catch (error) {
        this.showDanger(error)
      } finally {
        this.$wait.end('loadingMeasurement')
      }
    },

    async sendFile() {
      if (!this.$refs.calibrationForm.validate()) {
        return
      }

      if (this.isRoadroidApi) {
        this.importMeasurement()
        return
      }

      try {
        let data
        let file

        this.submitButtonLoading = true
        this.$wait.start('loadingMeasurement')

        if (this.selectedType === iriFileTypes.romdas) {
          data = await romdasPreProcessing(this.files[0])
          file = [new Blob([JSON.stringify(data)])]
        } else {
          file = this.files
        }

        await this.uploadIriData({
          files: file,
          type: this.selectedType,
          vehicle: this.selectedVehicle,
          device: this.selectedDevice,
          isReference: this.isReference,
          isPublic: this.isPublic,
          isReversed: data?.isReversed || false,
          layer: this.selectedLayer,
          roadCategory: this.selectedRoadCategory,
          roadWidth: this.roadWidth,
          shoulderWidth: this.shoulderWidth,
          bikeLaneWidth: this.bikeLaneWidth,
          importMacrotexture: this.macrotextureImport,
          direction: this.selectedDirection,
          lane: this.selectedLaneNumber,
          sectionStart: this.sectionStart,
          startLocation: this.defaultMarkerUpdated ? this.marker : undefined,
        })

        this.$refs.calibrationForm.reset()

        this.macrotextureImport = false
        this.isReference = false
        this.isPublic = false
        this.submitButtonLoading = false

        this.previewMap = false
        this.preview = false
        this.defaultMarkerUpdated = false

        this.showSuccess(
          this.$t('views.iri-management.upload-form.upload-file-successful'),
        )
      } catch (error) {
        this.showDanger(error)
      } finally {
        this.$wait.end('loadingMeasurement')
      }
    },

    async previewFile() {
      try {
        let data
        let files
        this.submitButtonLoading = true
        this.previewMap = false
        this.previewCompleted = false
        this.preview = false

        this.$wait.start('loadingMeasurement')

        if (this.selectedType === iriFileTypes.romdas) {
          data = await romdasPreProcessing(this.files[0])
          files = [new Blob([JSON.stringify(data)])]
        } else {
          files = this.files
        }

        await this.previewIriFile({
          type: this.selectedType,
          files: files,
        })

        if (
          this.selectedType !== iriFileTypes.romdas &&
          this.segments.length > 0
        ) {
          await this.getCalibrations({
            date: this.segments[0].date,
            disabled: false,
          })
        }

        this.showSuccess(
          this.$t('views.iri-management.upload-form.preview-file-successful'),
        )
        this.name = this.segments[0].name
        this.previewMap = true
        this.preview = true
        this.previewCompleted = true
      } finally {
        this.submitButtonLoading = false
        this.$wait.end('loadingMeasurement')
      }
    },

    onFileChange() {
      this.resetForm()

      if (this.files?.length > 0) {
        this.previewFile()
      }
    },

    validFile(v) {
      switch (this.selectedType) {
        case iriFileTypes.romdas:
        case iriFileTypes.roadroidSingle:
        case iriFileTypes.roadroidApiSingle:
          return (
            (!!v && v.length === 1) ||
            this.$t('api-error.iri.calibration-file-error')
          )
        default:
          return (
            (!!v && v.length === 3) ||
            this.$t('api-error.iri.roadroid-file-error')
          )
      }
    },

    onCalibrationSelect(calibrationId) {
      const calibration = this.calibrations.find(
        (_c) => _c._id === calibrationId,
      )
      this.selectedDevice = calibration.device
      this.selectedVehicle = calibration.vehicle
    },

    sectionStartRule(value) {
      const pattern = /^\d{1,4}\+\d{3}$/g
      return pattern.test(value) || this.$t('api-error.iri.sectionInput-error')
    },

    roadWidthRule(value) {
      if (!value && value !== 0) {
        return this.$t('api-error.iri.roadWidth-error')
      }

      if (!isValidNumber(value, 1, 100, 1)) {
        return this.$t('api-error.iri.roadWidth-error')
      }

      return true
    },

    shoulderWidthRule(value) {
      if (!value && value !== 0) {
        return this.$t('api-error.iri.shoulderWidth-error')
      }

      if (!isValidNumber(value, 0, 10, 1)) {
        return this.$t('api-error.iri.shoulderWidth-error')
      }

      return true
    },

    markerUpdated(event) {
      this.marker = event
      this.defaultMarkerUpdated = true
    },

    async onInput(input) {
      if (!input) {
        return
      }

      if (this.isRoadroidApiSingle && input.length !== 1) {
        return
      } else if (!this.isRoadroidApiSingle && input.length !== 3) {
        return
      }

      try {
        this.submitButtonLoading = true
        this.previewMap = false
        this.previewCompleted = false
        this.preview = false

        this.$wait.start('loadingMeasurement')

        await this.previewRoadroidMeasurements(input)

        if (
          this.selectedType !== iriFileTypes.romdas &&
          this.segments.length > 0
        ) {
          await this.getCalibrations({
            date: this.segments[0].date,
            disabled: false,
          })
        }

        this.showSuccess(
          this.$t('views.iri-management.upload-form.preview-file-successful'),
        )
        this.name = this.segments[0].name
        this.previewMap = true
        this.preview = true
        this.previewCompleted = true
      } catch (error) {
        this.showDanger(error)
      } finally {
        this.submitButtonLoading = false
        this.$wait.end('loadingMeasurement')
      }
    },

    deviceVehiclePairRule(value) {
      if (!value || !this.selectedDevice || !this.selectedVehicle) {
        return this.$t('api-error.iri.device-vehicle-pair-error')
      }
      return true
    },
  },
}
</script>
<style scoped>
.iri-map {
  grid-row: 1 / span 12;
  grid-column: 1 / span 8;
}
.iri-form {
  grid-row: 1 / span 12;
  grid-column: 9 / span 4;
}
</style>
